import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Tippy from '@tippyjs/react';
import Avatar from '../Avatar';
import {
  EditSchedule,
  ScheduleDetails,
  CreateIndividualSchedule,
  EditShiftTypes,
  DeleteSchedule,
} from './index';

const ScheduleCalender = ({
  isLoading,
  schedulesList,
  getSchedules,
  isRefetchEvents,
  handleReFetchEvents,
  handleDeleteSchedule,
  showEditSchedule,
  handleEditScheduleDisplay,
  exitingShifts,
  handleUpdateSchedule,
  showScheduleDetails,
  setShowScheduleDetails,
  handleCreateSchedule,
  showCreateIndividualSchedule,
  setShowCreateIndividualSchedule,
  handleShiftsDisplay,
  showShiftsModal,
  handleUpdateShifts,
  handleShowPublishSchedules,
  breaksList,
  hasMoreSchedules,
  handleSetPage,
  totalSchedules,
  handleShowCopySchedules,
  typesList,
  showDeleteSingleSchedule,
  handleShowDeleteSingleSchedules,
  handleCalendarLoading,
}) => {
  const calendarRef = useRef();
  const isFirstRender = useRef(true);

  // Memoize resourcesData
  const resourcesData = useMemo(() => {
    if (schedulesList.length === 0) return [];
    return schedulesList.map((resource) => ({
      id: resource.userId,
      title: `${resource.firstName} ${resource.lastName || ''}`,
      firstName: resource.firstName,
      lastName: resource.lastName || '',
      avatar: resource.avatar,
      bgColor: resource.bgColor,
      employeeId: resource.employeeId,
    }));
  }, [schedulesList]);

  const permissions = useSelector((state) => state.permissionsReducer);
  useEffect(() => {
    if (permissions && permissions.includes('team_member_view')) {
      // Add a listener to the 'viewDidMount' event
      const handleDatesSet = ({ view }) => {
        const isResourceTimelineWeek = view.type === 'resourceTimelineWeek';
        // Get the DOM element of the custom button
        const customButtonElement = document.querySelector(
          '.fc-myCustomButton-button'
        );

        // Show/hide the custom button based on the view type
        if (customButtonElement) {
          if (!isResourceTimelineWeek) {
            customButtonElement.style.display = 'none';
          } else {
            customButtonElement.removeAttribute('style');
          }
        }
      };

      const calendarApi = calendarRef.current.getApi();
      // Attach the listener to the 'viewDidMount' event
      calendarApi.on('datesSet', handleDatesSet);

      // Cleanup the listener when the component is unmounted
      return () => {
        calendarApi.off('datesSet', handleDatesSet);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tooltip content to display on hovering an event
  const tippyContent = useCallback((eventInfo) => {
    const { extendedProps } = eventInfo.event;
    return (
      <div>
        <p className="mb-0">Break(s) Info</p>
        {extendedProps.breaks?.length > 0 &&
          extendedProps.breaks.map((bItem, bItemIndex) => (
            <div key={bItemIndex + bItem.name}>
              <small>
                {bItem.name} {bItem.shortCode ? `(${bItem.shortCode}) ` : ' '}
                {`(${moment.utc(bItem.breakStart).format('hh:mm A')} - ${moment
                  .utc(bItem.breakEnd)
                  .format('hh:mm A')})`}
              </small>
            </div>
          ))}
      </div>
    );
  }, []);

  // Event block and content
  const renderEventContent = useCallback((eventInfo) => {
    if (eventInfo.event.extendedProps.isLeave) {
      return (
        <div className="p-2 text-wrap">
          <p className="calender-task-title mb-0">
            <strong className="text-capitalize">
              {eventInfo.event.extendedProps.scheduleName
                ? eventInfo.event.extendedProps.scheduleName
                : 'Leave'}
            </strong>
            {eventInfo.event.extendedProps.shiftStart &&
              eventInfo.event.extendedProps.shiftEnd && (
                <>
                  <br />
                  <span>
                    {`${moment
                      .utc(eventInfo.event.extendedProps.shiftStart)
                      .format('hh:mm A')} - ${moment
                      .utc(eventInfo.event.extendedProps.shiftEnd)
                      .format('hh:mm A')}`}
                  </span>
                </>
              )}
            <br />
            {!eventInfo.event.extendedProps.isPublished && (
              <span
                className="badge bg-light rounded-1"
                style={{ border: '1px solid rgba(0, 0, 0, .1)' }}
              >
                Draft
              </span>
            )}{' '}
          </p>
        </div>
      );
    }
    if (eventInfo.event.extendedProps.isHoliday) {
      return (
        <div className="p-2 text-wrap">
          <p className="calender-task-title mb-0">
            <strong className="text-capitalize">
              {eventInfo.event.extendedProps.scheduleName
                ? eventInfo.event.extendedProps.scheduleName
                : 'Holiday'}
            </strong>
            {eventInfo.event.extendedProps.shiftStart &&
              eventInfo.event.extendedProps.shiftEnd && (
                <>
                  <br />
                  <span>
                    {`${moment
                      .utc(eventInfo.event.extendedProps.shiftStart)
                      .format('hh:mm A')} - ${moment
                      .utc(eventInfo.event.extendedProps.shiftEnd)
                      .format('hh:mm A')}`}
                  </span>
                </>
              )}
            <br />
            {!eventInfo.event.extendedProps.isPublished && (
              <span
                className="badge bg-light rounded-1"
                style={{ border: '1px solid rgba(0, 0, 0, .1)' }}
              >
                Draft
              </span>
            )}{' '}
          </p>
        </div>
      );
    }
    if (eventInfo.event.extendedProps.isWeekend) {
      return (
        <div className="p-2 text-wrap">
          <p className="calender-task-title mb-0">
            <strong className="capitalize-first">
              {eventInfo.event.extendedProps.scheduleName
                ? eventInfo.event.extendedProps.scheduleName
                : 'Week off'}
            </strong>
            {eventInfo.event.extendedProps.shiftStart &&
              eventInfo.event.extendedProps.shiftEnd && (
                <>
                  <br />
                  <span>
                    {`${moment
                      .utc(eventInfo.event.extendedProps.shiftStart)
                      .format('hh:mm A')} - ${moment
                      .utc(eventInfo.event.extendedProps.shiftEnd)
                      .format('hh:mm A')}`}
                  </span>
                </>
              )}
            <br />
            {!eventInfo.event.extendedProps.isPublished && (
              <span
                className="badge bg-light rounded-1"
                style={{ border: '1px solid rgba(0, 0, 0, .1)' }}
              >
                Draft
              </span>
            )}{' '}
          </p>
        </div>
      );
    }
    if (
      eventInfo.event.extendedProps.breaks &&
      eventInfo.event.extendedProps.breaks.length > 0
    ) {
      return (
        <Tippy content={tippyContent(eventInfo)} theme="dark">
          <div className="p-2 text-wrap">
            <p className="calender-task-title mb-0">
              <strong className="text-capitalize">
                {eventInfo.event.extendedProps.scheduleName
                  ? eventInfo.event.extendedProps.scheduleName
                  : eventInfo.event.extendedProps.type}
              </strong>
              <br />
              <span>
                {`${moment
                  .utc(eventInfo.event.extendedProps.shiftStart)
                  .format('hh:mm A')} - ${moment
                  .utc(eventInfo.event.extendedProps.shiftEnd)
                  .format('hh:mm A')}`}
              </span>
              <br />
              {!eventInfo.event.extendedProps.isPublished && (
                <span
                  className="badge bg-light rounded-1"
                  style={{ border: '1px solid rgba(0, 0, 0, .1)' }}
                >
                  Draft
                </span>
              )}{' '}
              {eventInfo.event.extendedProps.breaks &&
                eventInfo.event.extendedProps.breaks.length > 0 && (
                  <span>
                    {eventInfo.event.extendedProps.breaks.length} Break(s)
                  </span>
                )}
            </p>
          </div>
        </Tippy>
      );
    }
    return (
      <div className="p-2 text-wrap">
        <p className="calender-task-title mb-0">
          <strong className="text-capitalize">
            {eventInfo.event.extendedProps.scheduleName
              ? eventInfo.event.extendedProps.scheduleName
              : eventInfo.event.extendedProps.type}{' '}
          </strong>
          <br />
          <span>
            {`${moment
              .utc(eventInfo.event.extendedProps.shiftStart)
              .format('hh:mm A')} - ${moment
              .utc(eventInfo.event.extendedProps.shiftEnd)
              .format('hh:mm A')}`}
          </span>
          <br />
          {!eventInfo.event.extendedProps.isPublished && (
            <span
              className="badge bg-light rounded-1"
              style={{ border: '1px solid rgba(0, 0, 0, .1)' }}
            >
              Draft
            </span>
          )}{' '}
          {eventInfo.event.extendedProps.breaks &&
            eventInfo.event.extendedProps.breaks.length > 0 && (
              <span>
                {eventInfo.event.extendedProps.breaks.length} Break(s)
              </span>
            )}
        </p>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Event resources
  const eventResource = useCallback(({ resource }) => {
    const { firstName, lastName, bgColor, avatar, employeeId } =
      resource._resource.extendedProps;
    return (
      <div className="d-flex align-items-center ms-2">
        <div className="avatar avatar-md me-2">
          <Avatar
            firstName={firstName}
            lastName={lastName}
            defaultBg={bgColor}
            imgPath={avatar}
          />
        </div>
        <span>
          {resource._resource.title}
          <br />
          {employeeId ? (
            <small className="text-muted text-capitalize">{employeeId}</small>
          ) : (
            ''
          )}
        </span>
      </div>
    );
  }, []);

  const updateEvents = useMemo(
    () => {
      const schedules = getSchedules();
      return schedules;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const refreshEvents = () => {
    const { current: calendarDom } = calendarRef;
    const calendarApi = calendarDom ? calendarDom.getApi() : null;
    if (calendarDom) {
      calendarApi.refetchEvents();
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    refreshEvents();
  }, [isRefetchEvents]);

  const [selectedEvent, setSelectedEvent] = useState({});
  const [selectedDate, setSelectedDate] = useState({});

  const handleEventClick = ({ event }) => {
    setSelectedEvent(event);
    setShowScheduleDetails(true);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowCreateIndividualSchedule(true);
  };

  const slotLabelContent = useCallback((arg) => {
    const date = moment.utc(arg.date);
    const formattedLabel = `${date.format('ddd')} ${date.format(
      'M'
    )}/${date.format('D')}`;
    return formattedLabel;
  }, []);

  return (
    <>
      <section className="schedule-calender-labels">
        <ul className="list-unstyled">
          <li>
            <Badge
              style={{
                backgroundColor: 'rgb(255, 199, 206)',
                color: 'rgb(156, 0, 6)',
              }}
            />
            <small>
              <strong>Unplanned Leave</strong>
            </small>
          </li>
          <li>
            <Badge style={{ backgroundColor: '#fbe3a6', color: '#3c495ab3' }} />
            <small>
              <strong>Late</strong>
            </small>
          </li>
          <li>
            <Badge style={{ backgroundColor: '#b6d9ad', color: '#3c495ab3' }} />
            <small>
              <strong>Expected</strong>
            </small>
          </li>
          <li>
            <Badge style={{ backgroundColor: '#4C9AFF', color: '#FFFFFF' }} />
            <small>
              <strong>Future shift</strong>
            </small>
          </li>
          <li>
            <Badge style={{ backgroundColor: '#fdefcd', color: '#3c495ab3' }} />
            <small>
              <strong>Overtime</strong>
            </small>
          </li>
          <li>
            <Badge style={{ backgroundColor: '#DEEBFF', color: '#3c495ab3' }} />
            <small>
              <strong>Holiday</strong>
            </small>
          </li>
          <li>
            <Badge style={{ backgroundColor: '#E0D7F8', color: '#5E3FA3' }} />
            <small>
              <strong>Leave</strong>
            </small>
          </li>
          <li>
            <Badge style={{ backgroundColor: '#E0E0E0', color: '#4C4C4C' }} />
            <small>
              <strong>Week off</strong>
            </small>
          </li>
        </ul>
      </section>
      <Card className="card-calendar">
        <Card.Body className="p-0">
          <section className="hd-calendar">
            {permissions && permissions.includes('team_member_view') ? (
              <>
                <FullCalendar
                  schedulerLicenseKey="0974720180-fcs-1683655338"
                  timeZone={false}
                  ref={calendarRef}
                  height={700}
                  plugins={[
                    dayGridPlugin,
                    interactionPlugin,
                    resourceTimelinePlugin,
                  ]}
                  customButtons={{
                    myCustomButton: {
                      text: 'Copy Schedule',
                      click() {
                        handleShowCopySchedules(true);
                      },
                    },
                    publishButton: {
                      text: 'Publish Schedule',
                      click() {
                        handleShowPublishSchedules(true);
                      },
                      className: 'btn btn-primary',
                    },
                    refresh: {
                      text: '',
                      icon: ' mdi mdi-refresh',
                      click() {
                        handleReFetchEvents();
                      },
                      title: 'Refresh',
                      hint: 'Refresh',
                      className: 'btn btn-sm btn-primary',
                    },
                    loadMore: {
                      text: 'Load More',
                      click() {
                        handleSetPage();
                      },
                      className: 'btn btn-sm btn-primary',
                    },
                    pageInfo: {
                      text: `Showing 1 - ${schedulesList.length} of
                              ${totalSchedules}`,
                      className: 'btn btn-sm btn-link disabled',
                    },
                  }}
                  headerToolbar={{
                    left: 'resourceTimelineMonth resourceTimelineWeek resourceTimelineDay refresh',
                    center: 'title',
                    right: `${
                      permissions && permissions.includes('schedule_publish')
                        ? 'publishButton myCustomButton prev next'
                        : 'prev next'
                    }`,
                  }}
                  footerToolbar={
                    hasMoreSchedules && !isLoading
                      ? {
                          center: 'loadMore',
                          left: 'pageInfo',
                        }
                      : null
                  }
                  eventOrder={(a, b) =>
                    a.extendedProps.shiftStart - b.extendedProps.shiftStart
                  }
                  initialView="resourceTimelineWeek"
                  resourceAreaHeaderClassNames="px-3"
                  resourceAreaHeaderContent="Team Members"
                  resourceOrder="title"
                  resources={resourcesData}
                  resourceAreaWidth="23%"
                  resourceLabelContent={eventResource}
                  slotDuration={{ days: 1 }}
                  slotLabelContent={slotLabelContent}
                  dayMaxEvents={2}
                  defaultAllDay
                  views={{
                    resourceTimelineDay: {
                      // @ToDo: Hour wise display on day view
                      // slotDuration: '01:00',
                      // slotLabelFormat: [{ hour: 'numeric' }],
                      // defaultAllDay: false,
                    },
                  }}
                  events={updateEvents}
                  eventContent={renderEventContent}
                  eventClassNames="event-block"
                  eventDisplay="block"
                  eventClick={
                    permissions && permissions.includes('schedule_create')
                      ? handleEventClick
                      : null
                  }
                  dateClick={
                    permissions && permissions.includes('schedule_create')
                      ? handleDateClick
                      : null
                  }
                  lazyFetching={false}
                  loading={handleCalendarLoading}
                  // editable
                  // dayMaxEvents
                  // eventDrop={(e) => handleChangeDueDate(e)}
                  buttonIcons={{
                    prev: ' mdi mdi-arrow-left',
                    next: ' mdi mdi-arrow-right',
                  }}
                />
                {showScheduleDetails && (
                  <>
                    <ScheduleDetails
                      showScheduleDetails={showScheduleDetails}
                      setShowScheduleDetails={setShowScheduleDetails}
                      scheduleInfo={selectedEvent}
                      deleteSchedule={handleDeleteSchedule}
                      handleDeleteScheduleDisplay={
                        handleShowDeleteSingleSchedules
                      }
                      handleEditScheduleDisplay={handleEditScheduleDisplay}
                      setSelectedEvent={setSelectedEvent}
                    />
                    <EditSchedule
                      scheduleInfo={selectedEvent}
                      showEditSchedule={showEditSchedule}
                      handleEditScheduleDisplay={handleEditScheduleDisplay}
                      exitingShifts={exitingShifts}
                      handleUpdateSchedule={handleUpdateSchedule}
                      breaksList={breaksList}
                    />
                    <DeleteSchedule
                      scheduleInfo={selectedEvent}
                      showDeleteSchedule={showDeleteSingleSchedule}
                      handleDeleteScheduleDisplay={
                        handleShowDeleteSingleSchedules
                      }
                      handleDeleteSchedule={handleDeleteSchedule}
                    />
                  </>
                )}
                {showCreateIndividualSchedule && (
                  <>
                    <CreateIndividualSchedule
                      exitingShifts={exitingShifts}
                      showCalendarAddShift={showCreateIndividualSchedule}
                      setShowCalendarAddShift={setShowCreateIndividualSchedule}
                      dateInfo={selectedDate}
                      handleCreateSchedule={handleCreateSchedule}
                      handleShiftsDisplay={handleShiftsDisplay}
                      breaksList={breaksList}
                      setSelectedEvent={setSelectedEvent}
                      typesList={typesList}
                    />
                    <EditShiftTypes
                      showShiftsModal={showShiftsModal}
                      handleShiftsDisplay={handleShiftsDisplay}
                      handleUpdateShifts={handleUpdateShifts}
                      exitingShifts={exitingShifts}
                    />
                  </>
                )}
              </>
            ) : (
              <FullCalendar
                schedulerLicenseKey="0974720180-fcs-1683655338"
                timeZone={false}
                ref={calendarRef}
                height={700}
                plugins={[dayGridPlugin]}
                headerToolbar={{
                  left: 'dayGridMonth dayGridWeek dayGridDay',
                  center: 'title',
                  right: 'prev next',
                }}
                footerToolbar={
                  hasMoreSchedules && !isLoading
                    ? {
                        center: 'loadMore',
                        left: 'pageInfo',
                      }
                    : null
                }
                initialView="dayGridMonth"
                dayMaxEvents={3}
                defaultAllDay
                events={updateEvents}
                eventContent={renderEventContent}
                eventDisplay="block"
                eventOrder={(a, b) =>
                  a.extendedProps.shiftStart - b.extendedProps.shiftStart
                }
                loading={handleCalendarLoading}
                buttonIcons={{
                  prev: ' mdi mdi-arrow-left',
                  next: ' mdi mdi-arrow-right',
                }}
              />
            )}
          </section>
        </Card.Body>
      </Card>
    </>
  );
};

ScheduleCalender.propTypes = {
  isLoading: PropTypes.bool,
  schedulesList: PropTypes.array,
  getSchedules: PropTypes.func,
  isRefetchEvents: PropTypes.bool,
  handleReFetchEvents: PropTypes.func,
  handleDeleteSchedule: PropTypes.func,
  showEditSchedule: PropTypes.bool,
  handleEditScheduleDisplay: PropTypes.func,
  exitingShifts: PropTypes.array,
  handleUpdateSchedule: PropTypes.func,
  showScheduleDetails: PropTypes.bool,
  setShowScheduleDetails: PropTypes.func,
  handleCreateSchedule: PropTypes.func,
  showCreateIndividualSchedule: PropTypes.bool,
  setShowCreateIndividualSchedule: PropTypes.func,
  handleShiftsDisplay: PropTypes.func,
  showShiftsModal: PropTypes.bool,
  handleUpdateShifts: PropTypes.func,
  handleShowPublishSchedules: PropTypes.func,
  breaksList: PropTypes.array,
  hasMoreSchedules: PropTypes.bool,
  handleSetPage: PropTypes.func,
  totalSchedules: PropTypes.number,
  handleShowCopySchedules: PropTypes.func,
  typesList: PropTypes.array,
  showDeleteSingleSchedule: PropTypes.bool,
  handleShowDeleteSingleSchedules: PropTypes.func,
  handleCalendarLoading: PropTypes.func,
};

export default ScheduleCalender;
